import { useEffect, useState } from "react";
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Header from '../../components/Header';
import ResponsiveTableRegion from '../../components/ResponsiveTableRegion';
import { ventrisConfig } from '../../ventris.config';

import GroupPageIcon from '../../icons/GroupPageIcon';
import { getUseCaseTerminology } from '../../useCase';
import { userHasResultsAccess } from '../../assessmentAccess';

type ClinicianClassesProps = {
};

function ClinicianClasses({}: ClinicianClassesProps) {
  const terms = getUseCaseTerminology();
  const [classes, setClasses] = useState([] as any);
  const [viewTab, setViewTab] = useState("view-all");
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(20);
  const [totalSize, setTotalSize] = useState(0);
  const [sortFieldName, setSortFieldName] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    loadClasses(1);
  }, []);

  useEffect(() => {
    loadClasses(page);
  }, [page, sizePerPage, sortFieldName, sortDirection, debouncedSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
      if (page !== 1) {
        setPage(1);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [search]);

  const loadClasses = (pageNumber: number) => {
    const token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/classes?page=' + pageNumber + '&sizePerPage=' + sizePerPage + '&sortField=' + sortFieldName + '&sortDirection=' + sortDirection + '&search=' + encodeURIComponent(debouncedSearch), {
      method: 'get',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setClasses(data.classes || []);
      setTotalSize(data.total_records || 0);
    });
  }

  const loadClass = (classId: any) => {
    document.location.href = "/class/" + classId;
  }

  const actionFormatter = (cell: any, row: any) => {
    return (
      <Button variant="link" size="sm" onClick={() => loadClass(row.id)}>View</Button>
    );
  }

  const classNameFormatter = (cell: any, row: any) => {
    return (
      <Button variant="link" size="sm" onClick={() => loadClass(row.id)}>{cell}</Button>
    );
  }

  const teacherFormatter = (cell: any) => {
    return cell && cell !== "" ? cell : "Unassigned";
  }

  const countFormatter = (cell: any) => {
    return cell ?? 0;
  }

  const columns = [
    {
      dataField: 'name',
      text: `${terms.class} Name`,
      sort: true,
      formatter: classNameFormatter
    },
    {
      dataField: 'school_name',
      text: 'School',
      sort: true
    },
    {
      dataField: 'teacher_names',
      text: terms.teacher,
      sort: true,
      formatter: teacherFormatter
    },
    {
      dataField: 'grade',
      text: 'Grade',
      sort: true
    },
    {
      dataField: 'student_count',
      text: `Number of ${terms.students}`,
      sort: true,
      formatter: countFormatter
    },
    {
      dataField: 'test_count',
      text: 'Number of Tests',
      sort: true,
      formatter: countFormatter
    },
    {
      dataField: 'id',
      text: 'View',
      formatter: actionFormatter
    }
  ];

  const handleTableChange = (type: string, { page, sizePerPage, sortField, sortOrder }: any) => {
    if ((sortField && sortFieldName !== sortField) || (sortDirection && sortDirection !== sortOrder)) {
      setPage(1);
    } else {
      setPage(page);
    }
    setSizePerPage(sizePerPage);
    setSortFieldName(sortField ? sortField : "");
    setSortDirection(sortOrder ? sortOrder : "");
  }

  return (
    <Container fluid className="ventris-content-wrapper ventris-classes">
      <Header portal={sessionStorage.getItem("portal")!} page="classes" />
      <Container as="main" id="main-content" tabIndex={-1}>
        <Row>
          <Col>
            <div className="classes-page-shell">
              <div className="classes-toolbar">
                <h1 className="page-header page-header-no-margin align-items-center">
                  <GroupPageIcon />
                  <div className="flex-grow">{terms.classes}</div>
                </h1>
                <div className="classes-toolbar-actions">
                  <Form.Control type="search" className="ventris-search classes-search" aria-label={`Search ${terms.classes.toLowerCase()}`} placeholder={`Search ${terms.classes.toLowerCase()}`} value={search} onChange={(event) => setSearch(event.target.value)} />
                  {userHasResultsAccess(JSON.parse(sessionStorage.getItem("current_user") || "{}")) ? <Button variant="primary" className="ventris-button-primary classes-results-button" onClick={() => document.location.href = "/tests"}>
                    View Results
                  </Button> : null}
                </div>
              </div>

              <ResponsiveTableRegion label={terms.classes} className="ventris-reports">
                <Tabs activeKey={viewTab} onSelect={(eventKey) => setViewTab(eventKey || "view-all")}>
                  <Tab eventKey="view-all" title="View All">
                  </Tab>
                </Tabs>
                <BootstrapTable
                  keyField='id'
                  data={classes}
                  columns={columns}
                  bordered={true}
                  remote={{pagination: true, sort: true}}
                  pagination={paginationFactory({ page, sizePerPage, totalSize })}
                  onTableChange={handleTableChange}
                />
              </ResponsiveTableRegion>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default ClinicianClasses;
